/* eslint-disable no-use-before-define */
import Vue from 'vue';
import Router from 'vue-router';

// Entry point
import Login from '@/pages/Login/Login';
import ExternalLogin from '@/pages/ExternalLogin/ExternalLogin';

/**
 * Prefetched modules to optimize UX (commonly used routes)
 * Reference: https://medium.com/webpack/link-rel-prefetch-preload-in-webpack-51a52358f84c
 */
const Module = () => import(
  /* webpackPrefetch: true, webpackChunkName: "page_module" */ '@/pages/Module/Module'
);
const Dashboard = () => import(
  /* webpackPrefetch: true, webpackChunkName: "page_dashboard" */ '@/pages/Dashboard/Dashboard'
);

const Style = () => import(/* webpackChunkName: "page_style" */ '@/pages/Style/Style');
const Notfound = () => import(/* webpackChunkName: "page_notfound" */ '@/pages/Notfound/Notfound');
const Disabled = () => import(/* webpackChunkName: "page_disabled" */ '@/pages/Disabled/Disabled');
const Blocked = () => import(/* webpackChunkName: "page_blocked" */ '@/pages/Blocked/Blocked');
const ChangePassword = () => import(
  /* webpackChunkName: "page_changepassword" */ '@/pages/ChangePassword/ChangePassword'
);
const Register = () => import(/* webpackChunkName: "page_register" */ '@/pages/Register/Register');
const DirectScript = () => import(
  /* webpackChunkName: "page_directscript" */ '@/pages/DirectScript/DirectScript'
);
const Menu = () => import(/* webpackChunkName: "page_menu" */ '@/pages/Menu/Menu');
const SingleScript = () => import(
  /* webpackChunkName: "page_script" */ '@/pages/SingleScript/SingleScript'
);
const StandaloneApp = () => import(
  /* webpackChunkName: "page_standaloneapp" */ '@/pages/StandaloneApp/StandaloneApp'
);
const StandaloneBff = () => import(
  /* webpackChunkName: "page_standalonebff" */ '@/pages/StandaloneBff/StandaloneBff'
);
const StandaloneBffClean = () => import(
  /* webpackChunkName: "page_standalonebff" */ '@/pages/StandaloneBffClean/StandaloneBffClean'
);
const Playground = () => import(
  /* webpackChunkName: "page_playground" */ '@/pages/Playground/Playground'
);
const Liveshop = () => import(
  /* webpackChunkName: "page_liveshop" */ '@modules/live-shop/customerb2c.vue'
);
const TermsConditions = () => import(
  /* webpackChunkName: "terms_conditions" */ '@/pages/TermsConditions/TermsConditions'
);
const LiveShopGroupers = () => import(
  /* webpackChunkName: "liveshop_groupers" */ '@modules/live-shop/bff/Groupers/Groupers.vue'
);
const LiveShopGlobalSearch = () => import(
  /* webpackChunkName: "liveshop_global_search" */ '@modules/live-shop/bff/GlobalSearch/GlobalSearch.vue'
);
const LiveShopTransmission = () => import(
  /* webpackChunkName: "liveshop_transmission" */ '@modules/live-shop/bff/Transmission/Transmission.vue'
);
const LiveShopTransmissionLive = () => import(
  /* webpackChunkName: "liveshop_transmission" */ '@modules/live-shop/bff/TransmissionLive/TransmissionLive.vue'
);
const LiveShopTransmissionProduct = () => import(
  /* webpackChunkName: "liveshop_transmission_product" */ '@modules/live-shop/bff/TransmissionProduct/TransmissionProduct.vue'
);
const LiveShopDashboardChart = () => import(
  /* webpackChunkName: "liveshop_dashboard_chart" */ '@modules/live-shop/bff/DashboardCharts/DashboardCharts.vue'
);
const LiveShopFilterTag = () => import(
  /* webpackChunkName: "liveshop_filter_tag" */ '@modules/live-shop/bff/FilterTag/FilterTag.vue'
);
const LiveShopListCartsAdmin = () => import(
  /* webpackChunkName: "liveshop_list_carts_admin" */ '@modules/live-shop/bff/ListCartsAdmin/ListCartsAdmin.vue'
);
const LiveShopListCampaigns = () => import(
  /* webpackChunkName: "liveshop_list_campaigns" */ '@modules/live-shop/bff/ListCampaigns/ListCampaigns'
);
const LiveShopCatalog = () => import(
  /* webpackChunkName: "liveshop-catalog" */ '@modules/live-shop/bff/Catalog/Catalog'
);
const LiveShopOrderImport = () => import(
  /* webpackChunkName: "liveshop-order-import" */ '@modules/live-shop/bff/OrderImport/OrderImport'
);
const LiveShopJoin = () => import(
  /* webpackChunkName: "liveshop-join" */ '@modules/live-shop/bff/JoinLive/JoinLive'
);
const LiveShopAccreditation = () => import(
  /* webpackChunkName: "bff_accreditation" */ '@modules/live-shop/bff/Accreditation/Accreditation'
);
const LiveShopAdminConfig = () => import(
  /* webpackChunkName: "liveshop-admin-config" */ '@modules/live-shop/admin-config.vue'
);
const Welcome = () => import(/* webpackChunkName: "page_welcome" */ '@/pages/Welcome/Welcome');
const ThankYou = () => import(/* webpackChunkName: "page_thank_you" */ '@/pages/Welcome/ThankYou');
const LeadCaptureDynamicForm = () => import(
  /* webpackChunkName: "page_thank_you" */ '@/pages/Welcome/LeadCaptureDynamicForm'
);
const NotificationsAcceptance = () => import(
  /* webpackChunkName: "page_thank_you" */ '@/pages/NotificationsAcceptance/NotificationsAcceptance'
);
const EntitiesValidation = () => import(
  /* webpackChunkName: "page_entities_validation" */ '@/pages/EntitiesValidation/EntitiesValidation'
);
const DynamicForm = () => import(
  /* webpackChunkName: "page_dynamicform" */ '@/pages/DynamicForm/DynamicForm'
);
const ProductDetails = () => import(
  /* webpackChunkName: "product_details" */ '@modules/content-delivery-network/views/ProductDetails'
);
const Help = () => import(/* webpackChunkName: "page_help" */ '@/pages/Help/Help');
const FederationLogin = () => import(
  /* webpackChunkName: "page_federation_login" */ '@/pages/FederationLogin/FederationLogin'
);
const LiveShopGenerateCatalogs = () => import(
  /* webpackChunkName: "page_generate_catalogs" */ '@modules/live-shop/bff/GenerateCatalogs/GenerateCatalogs.vue'
);

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: '*',
      redirect: '/',
    },
    {
      path: '/',
      name: 'Login',
      component: Login,
    },
    {
      path: '/termos/:id/:version',
      name: 'termsConditions',
      component: TermsConditions,
    },
    {
      path: '/session/:code',
      name: 'ExternalLogin',
      component: ExternalLogin,
    },
    {
      path: '/style',
      name: 'Style',
      component: Style,
    },
    {
      path: '/404',
      name: '404',
      component: Notfound,
    },
    {
      path: '/changePassword',
      name: 'ChangePassword',
      component: ChangePassword,
    },
    {
      path: '/register',
      name: 'Register',
      component: Register,
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Dashboard',
          component: Module,
        },
      ],
    },
    {
      path: '/directScript',
      name: 'DirectScript',
      component: DirectScript,
    },
    {
      path: '/menu',
      name: 'Menu',
      component: Menu,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Menu',
          component: Module,
        },
      ],
    },
    {
      path: '/script',
      name: 'SingleScript',
      component: SingleScript,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_Script',
          component: Module,
        },
      ],
    },
    {
      path: '/app',
      name: 'StandaloneApp',
      component: StandaloneApp,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ':moduleId/:itemId?',
          name: 'Module_StandaloneApp',
          component: Module,
        },
      ],
    },
    {
      path: '/playground',
      name: 'Playground',
      component: Playground,
    },
    {
      path: '/disabled',
      name: 'Disabled',
      component: Disabled,
      beforeEnter: (to, from, next) => {
        /**
         * Se o app não estiver desabilitado,
         * redireciona para a página de login
         */
        if (!isAppDisabled()) return next('/');
        return next();
      },
    },
    {
      path: '/blocked',
      name: 'Blocked',
      component: Blocked,
      beforeEnter: (to, from, next) => {
        /**
         * Se o dominio não estiver bloqueado,
         * redireciona para a página de login
         */
        if (!isAppDomainBlocked(to.query)) return next('/');

        return next();
      },
    },
    {
      path: '/(aovivo|live)/:id',
      name: 'LiveshopB2C_A',
      component: Liveshop,
      meta: {
        publicRoute: true,
      },
      props: route => ({ publicRouteId: route.params.id }),
    },
    {
      path: '/bff',
      name: 'StandaloneBff',
      component: StandaloneBff,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'live',
          name: 'BffLiveShopListCampaigns',
          component: LiveShopListCampaigns,
        },
        {
          path: 'live/:id',
          name: 'BffLiveShopListCartsAdmin',
          component: LiveShopListCartsAdmin,
        },
        {
          path: 'live/:id/catalog',
          name: 'BffLiveShopCatalog',
          component: LiveShopCatalog,
        },
        {
          path: 'live/:id/join',
          name: 'BffLiveShopJoin',
          component: LiveShopJoin,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/order-import',
          name: 'BffOrderConference',
          component: LiveShopOrderImport,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/groupers',
          name: 'BffLiveShopGroupers',
          component: LiveShopGroupers,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/global-search',
          name: 'BffLiveShopGlobalSearch',
          component: LiveShopGlobalSearch,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/filter-tag/:uuidTag',
          name: 'BffLiveShopFilterTag',
          component: LiveShopFilterTag,
        },
        {
          path: 'live/:id/generate-catalogs',
          name: 'BffLiveShopGenerateCatalogs',
          component: LiveShopGenerateCatalogs,
        },
        {
          path: 'live/:id/accreditation',
          name: 'BffLiveShopAccreditation',
          component: LiveShopAccreditation,
        },
        {
          path: 'legacy-config',
          name: 'LiveShopAdminConfig',
          component: LiveShopAdminConfig,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/dashboard-charts',
          name: 'BffLiveShopDashboardChart',
          component: LiveShopDashboardChart,
        },
      ],
    },
    {
      path: '/bff',
      name: 'StandaloneBffClean',
      component: StandaloneBffClean,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/transmission',
          name: 'BffLiveShopTransmission',
          component: LiveShopTransmission,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/transmission-product',
          name: 'BffLiveShopTransmissionProduct',
          component: LiveShopTransmissionProduct,
        },
        {
          path: 'live/:id/entity-code/:entityCode/cart-id/:cartId/transmission-live',
          name: 'BffLiveShopTransmissionLive',
          component: LiveShopTransmissionLive,
        },
      ],
    },
    {
      path: '/(aovivo|live)-*',
      name: 'LiveshopB2C_B',
      component: Liveshop,
      meta: {
        publicRoute: true,
      },
      props: route => ({
        publicRouteId: route.params[1] || route.params.pathMatch,
      }),
    },
    {
      path: '/welcome',
      name: 'Welcome',
      component: Welcome,
    },
    {
      path: '/welcome/thankyou',
      name: 'ThankYou',
      component: ThankYou,
    },
    {
      path: '/welcome/form/:hash',
      name: 'Form',
      component: LeadCaptureDynamicForm,
    },
    {
      path: '/notifications',
      name: 'Aceite de notificações',
      component: NotificationsAcceptance,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/entities-validation',
      name: 'Validações de Entidades',
      component: EntitiesValidation,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/dynamic-form/:hash',
      name: 'DynamicForm',
      component: DynamicForm,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/product-detail/type/:type/code/:code/image/:imageName',
      name: 'ProductDetails',
      component: ProductDetails,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/help',
      name: 'Help',
      component: Help,
      meta: {
        hybridRoute: true,
      },
    },
    {
      path: '/federationLogin/:appId*',
      name: 'federationLogin',
      component: FederationLogin,
      meta: {
        requiresAuth: false,
      },
    },
  ],
  mode: 'history',
});

const isAppBlocked = async (to, from, next) => {
  /**
   * REDIRECIONAMENTO CASO A APLICAÇÃO TENHA UMA MENSAGEM CUSTOMIZADA DE BLOQUEIO PARA A URL USADO ATUALMENTE
   */
  if (isAppDomainBlocked(to.query) && !brain.routerOnceEvaluated.blocked) {
    brain.routerOnceEvaluated.blocked = true;

    next({ path: '/blocked', query: to.query });
    return true;
  }

  /** Utilizado para garantir que não irá dar problemas com a QueryString ?dev=1 */
  if (!isAppDomainBlocked(to.query) && !brain.routerOnceEvaluated.blocked)
    brain.routerOnceEvaluated.blocked = true;

  return false;
};

const redirectIfIsAppDisabled = async (to, from, next) => {
  /**
   * REDIRECIONAMENTO CASO A APLICAÇÃO ESTEJA DESABILITADA
   */
  if (isAppDisabled() && to.name !== 'Disabled') {
    next('/disabled');
    return true;
  }

  return false;
};

const isNotProtectedRoute = async (to, from, next) => {
  /** Se a rota não for protegida, burla a etapa de validação de sessão */
  if (
    !to.matched.some(record => record.meta.requiresAuth)
    && to.path !== '/'
  ) {
    next();
    return true;
  }

  return false;
};

const handleHybridRoute = async next => {
  try {
    await brain.user.loginByToken();
    next();
    return true;
  } catch (err) {
    next();
    return true;
  }
};

const isHybridRoute = async to => to.matched.some(record => record.meta.hybridRoute) && to.path !== '/';

const isPublicRoute = async (to, from, next) => {
  /** Se a rota for isolada "deixa passar" */
  if (to.matched.some(record => record.meta.publicRoute)) {
    await brain.user.loginPublic();
    next();
    return true;
  }

  return false;
};

const isLoginRouteWithoutAuth = async (to, from, next) => {
  /** Verifica se não possui um cookie de sessão e foi redirecionado ao login */
  if (!brain.cookies.get('code') && to.path === '/') {
    next();
    return true;
  }

  return false;
};

const isAuthenticatedAndAccessingModule = async (to, from, next) => {
  /**
   * Caso esteja acessando um módulo já tendo realizado o login, não precisa validar sessão.
   */
  if (to.name && to.name.includes('Module') && brain.user.isLogged()) {
    /** Fecha scripts @legacy abertos */
    brain.closeScript();

    next();

    return true;
  }

  return false;
};

router.beforeEach(async (to, from, next) => {
  // console.log(moment().format(), '- beforeEach - to', to);
  // console.log(moment().format(), '- beforeEach - from', from);

  /**
   * Fecha todos os modais abertos ao trocar de rota,
   * evitando que mantenha o modal travando a tela
   */
  brain.closeAllModals();

  /**
   * Caso uma das validações sejam verdadeiras não deve ir para as proximas validações,
   * pois já realizou um 'next'
   * @important - todas as funções colocadas nessa sequencia devem dar next() ou retornar false!
   */
  if (await isPublicRoute(to, from, next)) return;
  if (await isAuthenticatedAndAccessingModule(to, from, next)) return;
  if (await isAppBlocked(to, from, next)) return;
  if (await redirectIfIsAppDisabled(to, from, next)) return;
  if (await isLoginRouteWithoutAuth(to, from, next)) return;
  if (await isHybridRoute(to)) return handleHybridRoute(next);
  if (await isNotProtectedRoute(to, from, next)) return;

  /** Fecha scripts @legacy abertos */
  brain.closeScript();

  try {
    if (!brain.user.isLogged()) {
      brain.portal.isLoaded = false;

      await brain.user.loginByToken();

      /** Abre o chat após o login */
      brain.openChat();

      brain.portal.isLoaded = true;
    } else {
      await brain.user.validateSession();
    }

    /**
     * REDIRECIONAMENTO CASO EXISTA UM ACESSO JÁ COM TOKEN
     */
    if (to.path === '/' && from.path === '/') return brain.sendToAppRoute();
    // Corrige um bug com duplicação de navegação (por ex: estar em dash e ir para dash)
    if (to.path === '/') return next(false);
  } catch (err) {
    brain.sessionError(to.fullPath);
  }

  return next();
});

/** Utilities */
const isAppDisabled = () => _.get(brain, 'company.json.status') === 'disabled';
const isAppDomainBlocked = query => {
  const domainOpened = _.get(brain, 'company.domain');
  return (
    _.get(brain, `company.json.message.blockedAccess['${domainOpened}']`)
    && query.dev !== '1'
  );
};

export default router;
